import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Stack,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PaymentIcon from "@mui/icons-material/Payment";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import useWorkspaceNavigation from "../hooks/useWorkspaceNavigation";
import {
  getDebtById,
  resetDebtServerStatus,
  recordDebtPayment,
} from "../redux/actions/debtActions";
import RecordPaymentModal from "../components/payments/RecordPaymentModal";
import DetailItem from "../components/common/DetailItem";
import StatusChip from "../components/common/StatusChip";
import { formatPlainNumber } from "../utils/appUtils";

const DebtPage: React.FC = (): React.JSX.Element | null => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { closeCurrentAndNavigate, navigateToTab } = useWorkspaceNavigation();

  const {
    debt: currentDebt,
    loadingFetchOne: loadingDebt,
    errorFetchOne: errorLoadingDebt,
    serverStatus,
    loadingPayment,
    errorPayment,
  } = useAppSelector((state) => state.debt);

  const [isPayOpen, setIsPayOpen] = useState<boolean>(false);
  const [debtId, setDebtId] = useState<null | string>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    const uuidRegex =
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
    const match = location.pathname.match(uuidRegex);

    if (match && match[0]) {
      setDebtId(match[0]);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (debtId) dispatch(getDebtById(debtId));
  }, [dispatch, debtId]);

  useEffect(() => {
    if (serverStatus?.status === 200) {
      setIsPayOpen(false);
      dispatch(resetDebtServerStatus());
    }
  }, [serverStatus, dispatch]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (): void => setMenuAnchorEl(null);

  if (loadingDebt)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  if (errorLoadingDebt)
    return <Alert severity="error">{errorLoadingDebt}</Alert>;
  if (!currentDebt) return null;

  const currency = currentDebt.currencyCode || "KES";

  // --- RENDER FUNCTIONS FOR ACTIONS ---
  const renderDesktopActions = (): React.ReactNode => {
    if (currentDebt.cleared) return null;
    return (
      <Button
        variant="contained"
        color="secondary"
        startIcon={<PaymentIcon />}
        onClick={() => setIsPayOpen(true)}
      >
        Record Payment
      </Button>
    );
  };

  const renderMobileActions = (): React.ReactNode => {
    if (currentDebt.cleared) return null;
    return (
      <Box>
        <IconButton onClick={handleMenuOpen} color="inherit">
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() => {
              setIsPayOpen(true);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <PaymentIcon fontSize="small" color="secondary" />
            </ListItemIcon>
            <ListItemText primary="Record Payment" />
          </MenuItem>
        </Menu>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "background.default",
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: 3,
          pb: 2,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center", mb: 1 }}
        >
          <Stack direction="row" sx={{ alignItems: "center", gap: 2 }}>
            <IconButton
              onClick={() => closeCurrentAndNavigate("/debts")}
              color="inherit"
              size="small"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              sx={{ fontWeight: "bold" }}
            >
              Debt Account: {currentDebt.customerName}
            </Typography>
            {!isMobile && (
              <StatusChip
                status={currentDebt.cleared ? "CLEARED" : "PENDING"}
              />
            )}
          </Stack>

          {/* Conditional Actions based on screen size */}
          {isMobile ? renderMobileActions() : renderDesktopActions()}
        </Stack>

        {isMobile && (
          <Box sx={{ mb: 2, pl: 5 }}>
            <StatusChip status={currentDebt.cleared ? "CLEARED" : "PENDING"} />
          </Box>
        )}

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ml: { xs: 0, sm: 6 } }}
        >
          Created: {new Date(currentDebt.date).toLocaleDateString()} • Branch:{" "}
          {currentDebt.branchName}
        </Typography>
      </Box>

      {/* BODY */}
      <Box sx={{ flex: 1, overflowY: "auto", p: { xs: 1.5, sm: 3 } }}>
        <Grid container spacing={3}>
          {/* LEFT COLUMN: Overview & Payments */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={3}>
              {/* FINANCIAL SUMMARY */}
              <Card
                elevation={0}
                sx={{ border: "1px solid", borderColor: "divider" }}
              >
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                    Financial Summary
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {/* Detail Item now explicitly appends the currency symbol */}
                  <DetailItem
                    label="Total Debt"
                    value={`${currency} ${formatPlainNumber(currentDebt.total)}`}
                  />
                  <Box sx={{ mt: 2 }}>
                    <DetailItem
                      label="Total Paid"
                      value={
                        <span style={{ color: "#4ade80", fontWeight: "bold" }}>
                          {currency}{" "}
                          {formatPlainNumber(currentDebt.totalPayments)}
                        </span>
                      }
                    />
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <DetailItem
                      label="Pending Balance"
                      value={
                        <span style={{ color: "#ef476f", fontWeight: "bold" }}>
                          {currency}{" "}
                          {formatPlainNumber(currentDebt.pendingAmount)}
                        </span>
                      }
                    />
                  </Box>

                  <Box
                    sx={{
                      mt: 3,
                      pt: 2,
                      borderTop: "1px dashed",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Source Document
                    </Typography>
                    <Button
                      size="small"
                      color="secondary"
                      sx={{ display: "block", mt: 0.5 }}
                      onClick={() =>
                        navigateToTab(`/receipts/${currentDebt.receiptId}`)
                      }
                    >
                      View Receipt #{currentDebt.receiptNumber}
                    </Button>
                  </Box>
                </CardContent>
              </Card>

              {/* PAYMENTS HISTORY */}
              <Card
                elevation={0}
                sx={{ border: "1px solid", borderColor: "divider" }}
              >
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                    Payment History
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {currentDebt.payments?.length > 0 ? (
                    <Stack spacing={2}>
                      {currentDebt.payments.map((p) => (
                        <Box
                          key={p.id}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: "bold" }}
                            >
                              {p.paymentMethodName}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block" }}
                            >
                              {" "}
                              {new Date(p.createdAt).toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="secondary">
                              Ref: {p.referenceCode || "N/A"}
                            </Typography>
                          </Box>
                          <Typography
                            sx={{ fontWeight: "bold", color: "success.main" }}
                          >
                            {formatPlainNumber(p.amount)}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No payments recorded.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Stack>
          </Grid>

          {/* RIGHT COLUMN: Items List */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Card
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                height: "100%",
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                  Items on Credit
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: "background.default" }}>
                      <TableRow>
                        <TableCell>Description</TableCell>
                        <TableCell align="center">Qty</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentDebt.items?.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: "bold" }}
                            >
                              {item.name}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">{item.qty}</TableCell>
                          <TableCell align="right">
                            {formatPlainNumber(item.price)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: "bold" }}>
                            {formatPlainNumber(item.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* PAYMENT MODAL */}
      {isPayOpen && (
        <RecordPaymentModal
          open={isPayOpen}
          onClose={() => setIsPayOpen(false)}
          entityId={currentDebt.id}
          entityType="Debt"
          documentNumber={currentDebt.receiptNumber}
          balance={currentDebt.pendingAmount}
          currencyCode={"N/A"}
          loading={loadingPayment}
          error={errorPayment}
          onSubmitPayment={(id, payload) =>
            dispatch(recordDebtPayment(id, payload))
          }
        />
      )}
    </Box>
  );
};
export default DebtPage;
